import { Box, TableCell } from "@mui/material";
import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { CellDivider } from "../cell-divider";
import { getLocalStorageItem, setLocalStorageItem } from "../../../utils";

const THROTTLE_THRESHOLD = 16;
const STORAGE_KEY = "col-width";

export const DataTableHeadCell = ({
  children,
  storageKey,
  columnKey,
}: PropsWithChildren<{ storageKey: string; columnKey: string }>) => {
  const localStorageKey = `${STORAGE_KEY}_${storageKey}_${columnKey}`;
  const widthFromStorage = getLocalStorageItem(localStorageKey);
  const [width, setWidth] = useState<null | number>(widthFromStorage || null);
  const [isResizing, setIsResizing] = useState(false);

  // Handle mouse down event for resizing
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    document.body.style.cursor = "col-resize";
    setIsResizing(true);
  };

  // Set initial width of the cell
  const setInitialWidth = useCallback(
    (element: HTMLElement) => {
      if (element && width === null) {
        setWidth(element.offsetWidth);
      }
    },
    [width]
  );

  // Handle mouse move and mouse up events for resizing
  useEffect(() => {
    let clientX = 0;
    let lastTime = 0;

    const handleResize = (e: MouseEvent) => {
      if (width === null) return;

      const currentTime = Date.now();
      if (currentTime - lastTime < THROTTLE_THRESHOLD) return;
      lastTime = currentTime;

      if (clientX === 0) {
        clientX = e.clientX;
      }
      const delta = e.clientX - clientX;
      const newWidth = width + delta;
      if (newWidth >= 100) {
        setWidth(newWidth);
        setLocalStorageItem(localStorageKey, newWidth);
      }
    };

    const handleResizeEnd = () => {
      document.body.style.cursor = "default";
      document.removeEventListener("mousemove", handleResize);
      document.removeEventListener("mouseup", handleResizeEnd);
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleResize);
      document.addEventListener("mouseup", handleResizeEnd);
    }
  }, [isResizing, width, localStorageKey]);

  return (
    <TableCell
      ref={setInitialWidth}
      sx={{ p: 1, minWidth: width ?? "auto", whiteSpace: "nowrap" }}
    >
      {children}
      <CellDivider />
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          cursor: "col-resize",
          opacity: 0.5,
          "&:hover": {
            background: (theme) => theme.palette.action.hover,
          },
          "&:active": {
            background: (theme) => theme.palette.action.selected,
          },
        }}
        onMouseDown={handleResizeStart}
      >
        <span
          style={{
            width: "5px",
            height: "100%",
            backgroundColor: "transparent",
            display: "block",
          }}
        />
      </Box>
    </TableCell>
  );
};
